import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeftIcon, PhoneIcon, EnvelopeIcon, CalendarDaysIcon,
  StarIcon, TicketIcon, BanknotesIcon, ArrowsRightLeftIcon,
  ClockIcon, ShieldCheckIcon, UserMinusIcon, ArrowPathIcon,
  CheckCircleIcon, XCircleIcon, UsersIcon, PlusIcon,
  ShareIcon, XMarkIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Avatar from '../components/Avatar';
import ConfirmModal from '../components/ConfirmModal';
import styles from './MemberDetailPage.module.css';

const fmtDate = d => { try { return format(new Date(d), 'dd MMM yyyy'); } catch { return '—'; } };
const fmtDT   = d => { try { return format(new Date(d), 'dd MMM yyyy, HH:mm'); } catch { return '—'; } };

function KV({ label, value, mono }) {
  return (
    <div className={styles.kv}>
      <span className={styles.kvLabel}>{label}</span>
      <span className={styles.kvValue} style={mono ? { fontFamily:'monospace', fontSize:12 } : {}}>{value ?? '—'}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = { active:'badge-active', expired:'badge-expired', pending:'badge-pending', inactive:'badge-inactive', success:'badge-success', failed:'badge-danger', used:'badge-inactive' }[status] || 'badge-inactive';
  return <span className={`badge ${cls}`}>{status}</span>;
}

const TIMELINE_ICONS = {
  active:   { Icon:CheckCircleIcon, color:'#10B981' },
  inactive: { Icon:UserMinusIcon,   color:'#EF4444' },
  expired:  { Icon:ClockIcon,       color:'#F59E0B' },
  pending:  { Icon:ArrowPathIcon,   color:'#2B9DD4' },
};

export default function MemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [confirm, setConfirm]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deactivating, setDea]        = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [family, setFamily]     = useState([]);
  const [famPurchases, setFamPurchases] = useState([]);
  const [showAddFamily, setShowAddFamily] = useState(false);
  const [famForm, setFamForm]   = useState({ name:'', phone:'', relationship:'spouse', dob:'' });

  const loadFamily = () => {
    adminAPI.getMemberFamily(id).then(r => {
      setFamily(r.family || []);
      setFamPurchases(r.purchases || []);
    }).catch(() => {});
  };

  useEffect(() => {
    adminAPI.getMember(id)
      .then(setData)
      .catch(e => { alert('Error: '+e.message); navigate('/members'); })
      .finally(() => setLoading(false));
    loadFamily();
  }, [id]);

  const handleAddFamily = async () => {
    if (!famForm.name) return;
    try {
      await adminAPI.addMemberFamily(id, famForm);
      setShowAddFamily(false);
      setFamForm({ name:'', phone:'', relationship:'spouse', dob:'' });
      loadFamily();
    } catch (e) { alert(e.message); }
  };

  const handleRevokeFamily = async (famId) => {
    if (!window.confirm('Revoke this family member access?')) return;
    try { await adminAPI.revokeMemberFamily(id, famId); loadFamily(); } catch (e) { alert(e.message); }
  };

  const handleReactivateFamily = async (famId) => {
    try { await adminAPI.reactivateMemberFamily(id, famId); loadFamily(); } catch (e) { alert(e.message); }
  };

  const FAMILY_QR_BASE = `${window.location.protocol}//${window.location.hostname}/family`;

  const handleDeactivate = async () => {
    setDea(true);
    try {
      await adminAPI.deactivateMember(id);
      const res = await adminAPI.getMember(id);
      setData(res);
    } catch (e) { alert('Error: '+e.message); } finally { setDea(false); setConfirm(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminAPI.deleteMember(id);
      navigate('/members');
    } catch (e) { alert('Error: '+e.message); } finally { setDeleting(false); setConfirmDelete(false); }
  };

  if (loading) return (
    <div>
      <div className="skeleton" style={{ height:180, borderRadius:16, marginBottom:20 }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20 }}>
        {[0,1,2].map(i=><div key={i} className="skeleton" style={{ height:200, borderRadius:16 }} />)}
      </div>
    </div>
  );

  const { user, memberships, payments, coupons, statusLogs = [], referrals = [], loyaltyBalance = 0 } = data || {};
  const activeMem = memberships?.find(m => m.status === 'active') || memberships?.[0];
  const isVIP = activeMem?.status === 'active';

  // Build timeline: status logs + payments merged, sorted by date desc
  const timeline = [
    ...(statusLogs||[]).map(l => ({ type:'status', date:l.changed_at, newStatus:l.new_status, oldStatus:l.old_status, source:l.source, notes:l.notes })),
    ...(payments||[]).map(p => ({ type:'payment', date:p.paid_at||p.created_at, amount:p.amount, paymentType:p.payment_type, status:p.payment_status })),
  ].sort((a,b) => new Date(b.date) - new Date(a.date));

  return (
    <div>
      {/* Back */}
      <button className={styles.backBtn} onClick={() => navigate('/members')}>
        <ArrowLeftIcon style={{ width:15, height:15 }} /> Back to Members
      </button>

      {/* Hero card */}
      <div className={`card ${styles.heroCard}`}>
        <div className={styles.heroLeft}>
          <Avatar firstName={user?.firstName} lastName={user?.lastName} size={72} />
          <div>
            <div className={styles.heroName}>{user?.firstName} {user?.lastName}</div>
            <div className={styles.heroMeta}>
              {user?.mobile && <span><PhoneIcon style={{ width:13, height:13 }} />{user.mobile}</span>}
              {user?.email  && <span><EnvelopeIcon style={{ width:13, height:13 }} />{user.email}</span>}
              {user?.createdAt && <span><CalendarDaysIcon style={{ width:13, height:13 }} />Joined {fmtDate(user.createdAt)}</span>}
            </div>
            <div className={styles.heroBadges}>
              {isVIP && <span className="badge badge-active"><StarIcon style={{ width:11, height:11 }} /> VIP Active</span>}
              {activeMem && (activeMem.bc_synced_at || activeMem.bcSyncedAt
                ? <span className="badge badge-active"><CheckCircleIcon style={{ width:11, height:11 }} /> BC Synced</span>
                : <span className="badge badge-warning"><ExclamationTriangleIcon style={{ width:11, height:11 }} /> Not in BC</span>)}
              {(activeMem?.member_code || activeMem?.memberCode) && <span className={styles.codeChip}>{activeMem.member_code || activeMem.memberCode}</span>}
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>
                <span style={{ fontWeight:700, color:'#F5C842' }}>{loyaltyBalance}</span> pts
              </span>
            </div>
          </div>
        </div>
        <div className={styles.heroActions}>
          {isVIP && (
            <button className="btn-danger" onClick={() => setConfirm(true)} disabled={deactivating}>
              <UserMinusIcon style={{ width:15, height:15 }} />
              {deactivating ? 'Deactivating…' : 'Deactivate'}
            </button>
          )}
          <button onClick={() => setConfirmDelete(true)} disabled={deleting}
            style={{ background:'none', border:'1.5px solid #EF4444', color:'#EF4444', borderRadius:8,
              padding:'6px 14px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex',
              alignItems:'center', gap:6, opacity: deleting ? 0.5 : 1 }}>
            <XCircleIcon style={{ width:14, height:14 }} />
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      {/* 3-col cards */}
      <div className={styles.threeCol}>
        {/* Membership */}
        <div className="card">
          <div className="card-title"><h2><StarIcon style={{ width:14, height:14, color:'#F5C842' }} /> Membership</h2></div>
          {activeMem ? (<>
            <KV label="Status"      value={<StatusBadge status={activeMem.status} />} />
            <KV label="Member Code" value={activeMem.member_code || activeMem.memberCode} mono />
            <KV label="Type"        value={activeMem.membership_type || activeMem.membershipType} />
            <KV label="Start Date"  value={fmtDate(activeMem.start_date || activeMem.startDate)} />
            <KV label="Expiry Date" value={fmtDate(activeMem.expiry_date || activeMem.expiryDate)} />
            <KV label="BC Sync"     value={
              (activeMem.bc_synced_at || activeMem.bcSyncedAt)
                ? <span title={`Confirmed in Business Central on ${fmtDT(activeMem.bc_synced_at || activeMem.bcSyncedAt)}`}
                    style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#16A34A', fontWeight:600 }}>
                    <CheckCircleIcon style={{ width:15, height:15 }} /> Synced
                  </span>
                : <span title="Not confirmed in Business Central yet"
                    style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#D97706', fontWeight:600 }}>
                    <ExclamationTriangleIcon style={{ width:15, height:15 }} /> Not synced
                  </span>
            } />
            <KV label="Renewals"    value={activeMem.renewal_count ?? activeMem.renewalCount ?? 0} />
          </>) : <div className="empty-state" style={{ padding:'20px 0' }}><p>No membership found</p></div>}
        </div>

        {/* Coupons */}
        <div className="card">
          <div className="card-title"><h2><TicketIcon style={{ width:14, height:14, color:'#2B9DD4' }} /> Coupons ({coupons?.length||0})</h2></div>
          {coupons?.length === 0
            ? <div className="empty-state" style={{ padding:'20px 0' }}><p>No coupons issued</p></div>
            : coupons?.slice(0,5).map((c,i) => (
              <div key={i} className={styles.couponRow}>
                <div>
                  <span className={styles.couponCode}>{c.code}</span>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{c.issue_reason}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:700 }}>{c.type==='percentage'?`${c.value}%`:`$${c.value}`}</div>
                  <StatusBadge status={c.status} />
                </div>
              </div>
            ))
          }
        </div>

        {/* Referrals */}
        <div className="card">
          <div className="card-title"><h2><ArrowsRightLeftIcon style={{ width:14, height:14, color:'#2B9DD4' }} /> Referrals ({referrals?.length||0})</h2></div>
          {referrals?.length === 0
            ? <div className="empty-state" style={{ padding:'20px 0' }}><p>No referrals yet</p></div>
            : referrals?.slice(0,5).map((r,i) => (
              <div key={i} className={styles.couponRow}>
                <div>
                  <div style={{ fontSize:12, fontWeight:600 }}>{r.referred_first||r.referred_mobile} {r.referred_last||''}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{fmtDate(r.created_at)}</div>
                </div>
                <div style={{ textAlign:'right', display:'flex', flexDirection:'column', gap:3 }}>
                  {r.registration_confirmed ? <span className="badge badge-active">Registered</span> : <span className="badge badge-pending">Pending</span>}
                  {r.reward_issued && <span className="badge badge-active">Rewarded</span>}
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {/* Family Members */}
      {isVIP && (
        <div className="card" style={{ marginTop:20 }}>
          <div className="card-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h2><UsersIcon style={{ width:14, height:14, color:'#2B9DD4' }} /> Family Cards ({family.filter(f=>f.is_active).length}/4)</h2>
            {family.filter(f=>f.is_active).length < 4 && (
              <button className="btn-primary" style={{ fontSize:12, padding:'6px 14px' }} onClick={() => setShowAddFamily(true)}>
                <PlusIcon style={{ width:14, height:14 }} /> Add Family Member
              </button>
            )}
          </div>

          {family.length === 0 ? (
            <div className="empty-state" style={{ padding:'20px 0' }}><p>No family members added yet</p></div>
          ) : (
            <div className={styles.familyGrid}>
              {family.map(fm => (
                <div key={fm.id} className={`${styles.familyRow} ${!fm.is_active ? styles.familyRowRevoked : ''}`}>
                  <div className={`${styles.familySlot} ${fm.is_active ? styles.familySlotActive : styles.familySlotRevoked}`}>
                    F{fm.slot}
                  </div>
                  <div className={styles.familyInfo}>
                    <div className={styles.familyName}>{fm.name}</div>
                    <div className={styles.familyMeta}>
                      {fm.relationship}{fm.phone ? ` — ${fm.phone}` : ''}{fm.dob ? ` — DOB: ${fmtDate(fm.dob)}` : ''}
                    </div>
                  </div>
                  {fm.is_active ? (
                    <div className={styles.familyActions}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize:11, padding:'6px 12px' }}
                        onClick={() => { const url = `${FAMILY_QR_BASE}/${fm.token}`; const ta = document.createElement('textarea'); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); alert('QR link copied!\n' + url); }}
                      >
                        <ShareIcon style={{ width:13, height:13 }} /> Copy Link
                      </button>
                      <button
                        className="btn-danger"
                        style={{ fontSize:11, padding:'6px 12px' }}
                        onClick={() => handleRevokeFamily(fm.id)}
                      >
                        <XMarkIcon style={{ width:13, height:13 }} /> Revoke
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-secondary"
                      style={{ fontSize:11, padding:'6px 12px' }}
                      onClick={() => handleReactivateFamily(fm.id)}
                    >
                      <ArrowUturnLeftIcon style={{ width:13, height:13 }} /> Reactivate
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Family purchases */}
          {famPurchases.length > 0 && (
            <div className={styles.familyPurchases}>
              <div className={styles.familyPurchasesLabel}>Family Purchases</div>
              <div className="table-wrap" style={{ borderRadius:10, overflow:'hidden' }}>
                <table className="data-table">
                  <thead><tr><th>Family Member</th><th>Store</th><th>Amount</th><th>OTP Verified</th><th>Date</th></tr></thead>
                  <tbody>
                    {famPurchases.map((p,i) => (
                      <tr key={i}>
                        <td style={{ fontWeight:600, fontSize:12 }}>{p.family_name} (F{p.slot})</td>
                        <td style={{ fontSize:12 }}>{p.store_name || '—'}</td>
                        <td style={{ fontWeight:700 }}>{p.amount ? `$${parseFloat(p.amount).toFixed(2)}` : '—'}</td>
                        <td>{p.otp_verified ? <span className="badge badge-active">Yes</span> : <span className="badge badge-pending">No</span>}</td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{fmtDT(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Add family modal */}
          {showAddFamily && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalCard}>
                <div className={styles.modalTitle}>Add Family Member</div>
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Name *</label>
                  <input className={styles.modalInput} value={famForm.name} onChange={e => setFamForm(f=>({...f, name:e.target.value}))} placeholder="Full name" />
                </div>
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Phone</label>
                  <input className={styles.modalInput} value={famForm.phone} onChange={e => setFamForm(f=>({...f, phone:e.target.value}))} placeholder="+961..." />
                </div>
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Date of Birth</label>
                  <input className={styles.modalInput} type="date" value={famForm.dob} onChange={e => setFamForm(f=>({...f, dob:e.target.value}))} />
                </div>
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Relationship *</label>
                  <select className={styles.modalSelect} value={famForm.relationship} onChange={e => setFamForm(f=>({...f, relationship:e.target.value}))}>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className={styles.modalBtns}>
                  <button className={styles.modalCancelBtn} onClick={() => setShowAddFamily(false)}>Cancel</button>
                  <button className={styles.modalConfirmBtn} onClick={handleAddFamily}>Add Member</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className={`card ${styles.timelineCard}`}>
          <div className="card-title"><h2><ClockIcon style={{ width:14, height:14, color:'var(--text-muted)' }} /> Activity Timeline</h2></div>
          <div className={styles.timeline}>
            {timeline.map((ev, i) => {
              if (ev.type === 'status') {
                const meta = TIMELINE_ICONS[ev.newStatus] || { Icon:ArrowPathIcon, color:'#64748B' };
                return (
                  <div key={i} className={styles.timelineItem}>
                    <div className={styles.timelineDot} style={{ background:`${meta.color}18`, border:`1px solid ${meta.color}30` }}>
                      <meta.Icon style={{ width:13, height:13, color:meta.color }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600 }}>
                        Status changed to <StatusBadge status={ev.newStatus} />
                        {ev.oldStatus && <span style={{ fontSize:11, color:'var(--text-muted)', marginLeft:6 }}>from {ev.oldStatus}</span>}
                      </div>
                      {ev.source && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>via {ev.source}</div>}
                    </div>
                    <div className={styles.timelineDate}>{fmtDT(ev.date)}</div>
                  </div>
                );
              }
              const ok = ev.status === 'success';
              return (
                <div key={i} className={styles.timelineItem}>
                  <div className={styles.timelineDot} style={{ background:ok?'#D1FAE5':'#FEE2E2', border:`1px solid ${ok?'#A7F3D0':'#FECACA'}` }}>
                    {ok ? <CheckCircleIcon style={{ width:13, height:13, color:'#10B981' }} /> : <XCircleIcon style={{ width:13, height:13, color:'#EF4444' }} />}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>
                      Payment {ev.paymentType} — <span style={{ color:ok?'#10B981':'#EF4444' }}>${parseFloat(ev.amount||0).toFixed(2)}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}><StatusBadge status={ev.status} /></div>
                  </div>
                  <div className={styles.timelineDate}>{fmtDT(ev.date)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payments table */}
      {payments?.length > 0 && (
        <div className="card" style={{ marginTop:20, padding:0, overflow:'hidden' }}>
          <div style={{ padding:'18px 20px', borderBottom:'1px solid var(--border)' }}>
            <h2 style={{ fontSize:14, fontWeight:700 }}><BanknotesIcon style={{ width:14, height:14, color:'var(--text-muted)', verticalAlign:'middle', marginRight:6 }} />Payment History</h2>
          </div>
          <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
            <table className="data-table">
              <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Provider</th><th>Status</th></tr></thead>
              <tbody>
                {payments.map((p,i) => (
                  <tr key={i}>
                    <td style={{ fontSize:12 }}>{fmtDT(p.paid_at||p.created_at)}</td>
                    <td style={{ textTransform:'capitalize', fontSize:12 }}>{p.payment_type}</td>
                    <td style={{ fontWeight:700 }}>${parseFloat(p.amount||0).toFixed(2)}</td>
                    <td style={{ fontSize:12, color:'var(--text-muted)' }}>{p.provider||'—'}</td>
                    <td><StatusBadge status={p.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title="Deactivate Membership"
          message={`Are you sure you want to deactivate ${user?.firstName}'s VIP membership? This will take effect immediately.`}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={handleDeactivate}
          onCancel={() => setConfirm(false)}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete Member"
          message={`Are you sure you want to permanently delete ${user?.firstName} ${user?.lastName}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
